export class TokenManager {
  private storage: 'memory' | 'session';
  private tokens: { access?: string; refresh?: string } = {};
  private refreshPromise: Promise<void> | null = null;
  private tokenExpiry: number | null = null;
  private refreshCallback: ((refreshToken: string) => Promise<{ token: string; refreshToken: string }>) | null = null;

  constructor() {
    // Use session storage in production to persist tokens across navigation
    const defaultStorage = import.meta.env.VITE_ENVIRONMENT === 'production' ? 'session' : 'memory';
    this.storage = (import.meta.env.VITE_AUTH_TOKEN_STORAGE || defaultStorage) as 'memory' | 'session';
    
    if (this.storage === 'session') {
      console.warn('Using sessionStorage for tokens - this is a security risk. Consider using memory storage in production.');
    }
    
    // Load existing tokens if using session storage
    if (this.storage === 'session') {
      const accessToken = sessionStorage.getItem('access_token');
      const refreshToken = sessionStorage.getItem('refresh_token');
      if (accessToken && refreshToken) {
        this.tokens = { access: accessToken, refresh: refreshToken };
        this.tokenExpiry = this.parseTokenExpiry(accessToken);
      }
    }
  }

  setRefreshCallback(callback: (refreshToken: string) => Promise<{ token: string; refreshToken: string }>) {
    this.refreshCallback = callback;
  }

  setTokens(access: string, refresh: string) {
    if (this.storage === 'memory') {
      this.tokens = { access, refresh };
    } else {
      // Temporary sessionStorage with security warning
      console.warn('Using sessionStorage for tokens - security risk');
      sessionStorage.setItem('access_token', access);
      sessionStorage.setItem('refresh_token', refresh);
      this.tokens = { access, refresh };
    }
    // Parse JWT to get expiry
    this.tokenExpiry = this.parseTokenExpiry(access);
  }

  getAccessToken(): string | null {
    // Check if token needs refresh (5 minutes before expiry)
    if (this.shouldRefresh() && this.refreshCallback) {
      // Don't wait for refresh, return current token
      this.refreshAccessToken();
    }
    
    return this.storage === 'memory' 
      ? this.tokens.access || null
      : sessionStorage.getItem('access_token');
  }

  getRefreshToken(): string | null {
    return this.storage === 'memory'
      ? this.tokens.refresh || null
      : sessionStorage.getItem('refresh_token');
  }

  async refreshAccessToken(): Promise<void> {
    if (this.refreshPromise) return this.refreshPromise;
    
    this.refreshPromise = this.doRefresh();
    try {
      await this.refreshPromise;
    } finally {
      this.refreshPromise = null;
    }
  }

  private async doRefresh(): Promise<void> {
    const refreshToken = this.getRefreshToken();
    if (!refreshToken || !this.refreshCallback) {
      throw new Error('No refresh token available or refresh callback not set');
    }

    try {
      const { token, refreshToken: newRefreshToken } = await this.refreshCallback(refreshToken);
      this.setTokens(token, newRefreshToken);
    } catch (error) {
      // Clear tokens on refresh failure
      this.clearTokens();
      throw error;
    }
  }

  clearTokens() {
    this.tokens = {};
    this.tokenExpiry = null;
    if (this.storage === 'session') {
      sessionStorage.removeItem('access_token');
      sessionStorage.removeItem('refresh_token');
    }
  }

  private shouldRefresh(): boolean {
    if (!this.tokenExpiry) return false;
    
    // Refresh if token expires in less than 5 minutes
    const now = Date.now() / 1000;
    const timeUntilExpiry = this.tokenExpiry - now;
    return timeUntilExpiry < 300; // 5 minutes
  }

  private parseTokenExpiry(token: string): number | null {
    try {
      // Parse JWT payload (base64url decode)
      const parts = token.split('.');
      if (parts.length !== 3) return null;
      
      const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')));
      return payload.exp || null;
    } catch {
      return null;
    }
  }

  isTokenExpired(): boolean {
    if (!this.tokenExpiry) return true;
    return Date.now() / 1000 >= this.tokenExpiry;
  }
}